import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CalendarDays, Pill, AlertCircle, MessageSquare, Bell, Settings, UserCircle2, ChevronRight, Clock, CheckCircle2, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import { getMedicationColor } from "@/lib/medicationColors";

/**
 * MobileHome — first screen patients see.
 * Per the requirements doc: show the two MOST PROMINENT alerts at the top
 * (next upcoming appointment + latest provider message), then a row of tabs.
 */
const TABS = [
  { to: "/m/appointments",  icon: CalendarDays, label: "Appointments", color: "bg-brand-500" },
  { to: "/m/medication",    icon: Pill,         label: "Medication",   color: "bg-warmth-500" },
  { to: "/m/effects",       icon: AlertCircle,  label: "Side Effects", color: "bg-rose-500" },
  { to: "/m/messages",      icon: MessageSquare,label: "Messages",     color: "bg-indigo-500" },
  { to: "/m/notifications", icon: Bell,         label: "Notifications", color: "bg-amber-500" },
  { to: "/m/settings",      icon: Settings,     label: "Settings",     color: "bg-stone-700" },
];

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function MobileHome() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [todayGroup, setTodayGroup] = useState(null);
  const [switchBusy, setSwitchBusy] = useState(false);
  const nav = useNavigate();

  const handleSignOut = () => {
    if (window.confirm("Sign out of OncoCadence?")) {
      logout();
      nav("/login");
    }
  };

  const load = async () => {
    const r = await api.get("/patient/mobile-dashboard");
    setData(r.data);
    const todayIso = new Date().toISOString().slice(0, 10);
    try {
      const a = await api.get("/patient/appointments");
      const all = [...(a.data.future || []), ...(a.data.past || [])].filter((e) => e.status !== "skipped");
      // Helper — pick the first calendar date that still has at least one
      // non-completed drug event (or any non-drug event). If today is fully
      // completed by the provider, advance to tomorrow / next day with work.
      const byDate = new Map();
      all.forEach((e) => {
        if (e.date < todayIso) return;
        if (!byDate.has(e.date)) byDate.set(e.date, []);
        byDate.get(e.date).push(e);
      });
      const sortedDates = [...byDate.keys()].sort();
      let chosen = null;
      for (const d of sortedDates) {
        const evs = byDate.get(d);
        const drugs = evs.filter((e) => e.event_type === "Drug Administration");
        const otherEvents = evs.filter((e) => e.event_type !== "Drug Administration");
        const allDone = drugs.length > 0 && drugs.every((e) => e.status === "completed" || e.med_status === "administered");
        // Skip past-today dates where every drug event is already completed
        // AND there are no other (Lab Draw / Clinic Visit) events to show.
        if (allDone && otherEvents.length === 0 && d === todayIso) continue;
        chosen = { date: d, events: evs };
        break;
      }
      setTodayGroup(chosen);
    } catch (_) { setTodayGroup(null); }
  };
  useEffect(() => { load(); }, []);

  const toggleTodayCompletion = async (group, completed) => {
    if (switchBusy) return;
    const calId = group.events[0]?.calendar_id;
    if (!calId) return;
    setSwitchBusy(true);
    try {
      await api.post(`/calendars/${calId}/day-completion`, { date: group.date, completed });
      await load();
    } finally { setSwitchBusy(false); }
  };

  if (!data) return <div className="max-w-md mx-auto p-6 text-stone-500">Loading…</div>;

  const { upcoming_appointment: upcoming, latest_unread_message: msg, unread_notifications, provider, patient } = data;
  // Two most prominent: (1) Today's grouped Medication Date Card when something
  // is due today, (2) next upcoming OR latest unread provider message.
  const alerts = [];
  if (todayGroup) alerts.push({ kind: "today_group", data: todayGroup });
  if (upcoming && (!todayGroup || upcoming.date !== todayGroup.date)) alerts.push({ kind: "upcoming", data: upcoming });
  if (msg && alerts.length < 2) alerts.push({ kind: "message", data: msg });
  const top2 = alerts.slice(0, 2);

  return (
    <div className="max-w-md mx-auto bg-stone-50 min-h-screen" data-testid="mobile-home">
      <header className="hero-gradient px-5 pt-7 pb-7">
        <div className="flex items-center justify-between">
          <Logo size={28} withWordmark />
          <div className="flex flex-col items-end gap-1.5">
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/85 hover:text-white font-semibold px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
              data-testid="mobile-signout-link"
            >
              <LogOut className="w-3.5 h-3.5" />Sign out
            </button>
            <button
              onClick={() => nav("/m/settings")}
              className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"
              data-testid="profile-shortcut"
            >
              <UserCircle2 className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-xs text-brand-200 uppercase tracking-wider">Hi {patient?.first_name || user?.full_name?.split(" ")[0] || "there"} 👋</div>
          <div className="font-display font-bold text-xl mt-0.5">Your treatment plan</div>
          {provider && (
            <div className="text-xs text-brand-100 mt-1">Care team — {provider.full_name}{provider.specialty ? ` · ${provider.specialty}` : ""}</div>
          )}
        </div>
      </header>

      {/* Two prominent alerts only */}
      <section className="px-5 pt-4 space-y-2.5" data-testid="top-alerts">
        {top2.length === 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-soft text-center text-stone-500 text-sm">
            All caught up — no immediate items.
          </div>
        )}
        {top2.map((a, i) => {
          if (a.kind === "today_group") {
            const g = a.data;
            const todayIso = new Date().toISOString().slice(0, 10);
            const isToday = g.date === todayIso;
            const drugEvents = g.events.filter((e) => e.event_type === "Drug Administration");
            const otherEvents = g.events.filter((e) => e.event_type !== "Drug Administration");
            const completed = drugEvents.length > 0 && drugEvents.every((e) => e.status === "completed" || e.med_status === "administered");
            const seen = new Set();
            const chips = drugEvents.filter((e) => {
              const k = e.print_name || e.drug_name || e.name;
              if (seen.has(k)) return false; seen.add(k); return true;
            }).map((e) => ({ event: e, name: e.print_name || e.drug_name || e.name, color: getMedicationColor(e.print_name || e.drug_name || e.name) }));
            const d = new Date(g.date + "T00:00:00");
            const confirmation = g.events.find((e) => e.confirmation)?.confirmation;
            return (
              <div
                key={i}
                onClick={() => nav("/m/appointments")}
                className={`cursor-pointer medication-date-card rounded-2xl p-4 ${completed ? "is-completed" : ""}`}
                data-testid="alert-today-group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider med-date-day font-semibold">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                      <div className="font-display font-bold text-stone-900 text-lg leading-tight">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                    </div>
                    {isToday
                      ? <span className="text-[10px] uppercase tracking-wider font-bold text-white bg-rose-500 px-2 py-0.5 rounded-full">Due Today</span>
                      : <span className="text-[10px] uppercase tracking-wider font-bold text-white bg-brand-500 px-2 py-0.5 rounded-full">Next Visit</span>
                    }
                    {confirmation && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                        confirmation === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        confirmation === "tentative" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-rose-50 text-rose-700 border-rose-200"
                      }`}>{confirmation[0].toUpperCase() + confirmation.slice(1)}</span>
                    )}
                  </div>
                  {completed && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                </div>
                {chips.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {chips.map((c) => (
                      <span key={c.name} className="px-2 py-1 rounded-lg text-[11px] font-bold truncate max-w-[140px]" style={{ backgroundColor: c.color.bg, color: c.color.text }} data-testid={`today-chip-${c.name.replace(/\W+/g, "-")}`}>{c.name}</span>
                    ))}
                  </div>
                )}
                {otherEvents.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {otherEvents.map((e) => (
                      <span key={e.id} className="px-2 py-1 rounded-lg text-[11px] font-medium bg-stone-100 text-stone-700 border border-stone-200">{e.event_type}</span>
                    ))}
                  </div>
                )}
                {drugEvents.length > 0 && completed && (
                  <div className="mt-3 flex items-center justify-between text-xs" onClick={(e) => e.stopPropagation()}>
                    <span className="font-semibold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Completed by your provider</span>
                  </div>
                )}
              </div>
            );
          }
          if (a.kind === "message") {
            return (
              <button key={i} onClick={() => nav("/m/messages")} className="w-full text-left bg-white border border-indigo-200 rounded-2xl p-4 shadow-soft" data-testid="alert-message">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><MessageSquare className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-indigo-600 font-semibold">New provider message</div>
                    <div className="font-semibold text-stone-900 truncate mt-0.5">{a.data.sender_name}</div>
                    <div className="text-sm text-stone-600 line-clamp-2">{a.data.body}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 mt-1" />
                </div>
              </button>
            );
          }
          const e = a.data;
          // a.kind === "upcoming" (today_group is handled above)
          return (
            <button key={i} onClick={() => nav(`/m/appointments`)} className="w-full text-left bg-white border rounded-2xl p-4 shadow-soft border-brand-200" data-testid={`alert-${a.kind}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-brand-100 text-brand-700">
                  <CalendarDays className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-brand-700">Next appointment</div>
                  <div className="font-semibold text-stone-900 truncate mt-0.5">{e.print_name || e.drug_name || e.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{shortDate(e.date)}</span>
                    {e.time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{e.time}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 mt-1" />
              </div>
            </button>
          );
        })}
      </section>

      {/* Tab grid (also accessible via bottom nav). All in one row when scrolled to top. */}
      <section className="px-5 mt-6" data-testid="tab-grid">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Quick access</div>
        <div className="grid grid-cols-3 gap-3">
          {TABS.map((t) => (
            <button key={t.to} onClick={() => nav(t.to)} className="bg-white border border-stone-200 rounded-2xl p-3 text-center shadow-soft active:scale-95 transition" data-testid={`tab-${t.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className={`w-10 h-10 rounded-full ${t.color} text-white mx-auto flex items-center justify-center`}>
                <t.icon className="w-5 h-5" />
              </div>
              <div className="mt-2 text-xs font-semibold text-stone-800">{t.label}</div>
              {t.to === "/m/notifications" && unread_notifications > 0 && (
                <div className="mt-0.5 text-[10px] text-rose-600 font-bold">{unread_notifications} new</div>
              )}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
